/**
 * Page chrome for every quiz route.
 *
 * Owns: back-link, header (icon + title + subtitle), description panel,
 * session stats row, optional bank-progress bar, and the outer surface
 * card. Routes provide `children` for the quiz-specific content.
 *
 * Two modes share this component:
 *
 *   - Procedural quizzes pass only `stats` (no `bankSize` / `isComplete` /
 *     `onRestart`). Layout renders chrome + children verbatim.
 *
 *   - Bank quizzes pass `bankPosition` + `bankSize` (progress bar shows)
 *     plus `isComplete` + `onRestart` (the completion panel takes over
 *     `children` when the quiz finishes — routes don't render their own
 *     branch).
 */

import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { BackLink } from "@/shared/components/BackLink";

import type { SessionStats } from "@/features/quiz/lib/common";
import { QuizCompletePanel } from "@/features/quiz/components/QuizCompletePanel";
import { SessionStatsRow } from "@/features/quiz/components/SessionStatsRow";

interface QuizLayoutProps {
  Icon: LucideIcon;
  title: string;
  subtitle: string;
  description: ReactNode;
  stats: SessionStats;
  /** Current 1-based position within the bank (undefined for procedural quizzes). */
  bankPosition?: number;
  /** Total questions in the bank (undefined for procedural quizzes). */
  bankSize?: number;
  /** When true, replaces `children` with the built-in completion panel.
   *  Only meaningful with `bankSize` + `onRestart`. */
  isComplete?: boolean;
  /** Restart handler — required if `isComplete` can become true. */
  onRestart?: () => void;
  children: ReactNode;
}

export function QuizLayout({
  Icon,
  title,
  subtitle,
  description,
  stats,
  bankPosition,
  bankSize,
  isComplete,
  onRestart,
  children,
}: QuizLayoutProps) {
  const showProgress = bankSize !== undefined && bankPosition !== undefined;
  // Complete state is only valid for bank quizzes (which always have bankSize + onRestart).
  const showComplete = isComplete && bankSize !== undefined && !!onRestart;
  return (
    <div className="max-w-2xl mx-auto px-4 md:px-8 py-8 space-y-6">
      <BackLink to="/quizzes" label="Quizzes" />

      <header className="flex items-center gap-3">
        <Icon size={22} strokeWidth={1.75} className="text-accent" />
        <div>
          <h1 className="text-2xl font-bold text-text tracking-tight">{title}</h1>
          <p className="text-sm text-text-muted">{subtitle}</p>
        </div>
      </header>

      <p className="rounded-xl border border-border bg-surface/50 px-4 py-3 text-sm text-text-muted leading-relaxed">
        {description}
      </p>

      {showProgress && <ProgressBar position={bankPosition!} size={bankSize!} />}

      <SessionStatsRow stats={stats} />

      <section className="rounded-2xl border border-border bg-surface p-6 md:p-8 space-y-6">
        {showComplete ? (
          <QuizCompletePanel bankSize={bankSize!} stats={stats} onRestart={onRestart!} />
        ) : (
          children
        )}
      </section>
    </div>
  );
}

function ProgressBar({ position, size }: { position: number; size: number }) {
  // Cap to the visible range (in case isComplete pushes position past
  // size + 1 in rare race conditions).
  const visiblePos = Math.min(position, size);
  const pct = (visiblePos / size) * 100;
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between text-xs text-text-muted">
        <span>Question {visiblePos} of {size}</span>
        <span>{Math.round(pct)}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-border overflow-hidden">
        <div
          className="h-full bg-accent transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
